import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import {
  BulkCreateProjectDocumentsDocument,
  ProjectDocumentsDataCollectionDocument,
  ProjectManagementProjectDetailsPageDocumentsPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ProjectDocumentsDataCollection from 'modules/project_management/components/tables/ProjectDocumentsDataCollection'
import { MissingFeatureEmptyState } from 'modules/project_management/components/upsells/MissingFeatureEmptyState'
import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'
import DocumentsUploadWizard from 'modules/project_management/components/wizards/AddDocumentWizard/DocumentsUploadWizard'

const DocumentsPage = () => {
  const { id: projectId } = useParams()
  const { employee } = useCurrentSession()
  const [showModal, setShowModal] = useState(false)

  const numberProjectId = projectId ?? ''

  useEffect(() => {
    track(trackingEvents.projectManagement.projectFiles.visitProjectFilesTab)
  }, [])

  const [createProjectDocument, { loading: createProjectDocumentLoading }] = useMutation(
    BulkCreateProjectDocumentsDocument,
    {
      extractorFn: (mutation) =>
        mutation.projectManagementMutations.bulkCreateProjectmanagementProjectDocument,
      refetchQueries: [
        ProjectDocumentsDataCollectionDocument,
        ProjectManagementProjectDetailsPageDocumentsPageDocument,
      ],
      awaitRefetchQueries: true,
    }
  )

  const handleSuccess = async (files: File[]) => {
    if (!projectId || !employee?.id) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('project_management.files.upload_error'),
      })
      return
    }

    const result = await createProjectDocument({
      variables: {
        files,
        projectId: numberProjectId,
        uploaderId: employee.id,
      },
    })

    track(trackingEvents.projectManagement.projectFiles.clickOnUploadConfirmation)
    setShowModal(false)

    app.setFlash({
      type: 'correct',
      message: i18n.t('project_management.files.upload_success'),
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.files.upload_success'),
      })
      setShowModal(false)
    }
  }

  const { hasPayingProjectsFeature } = useOutletContext<{
    hasPayingProjectsFeature: boolean
  }>()

  const { data } = useQuery(ProjectManagementProjectDetailsPageDocumentsPageDocument, {
    variables: {
      projectId: numberProjectId,
    },
  })

  const canManageDocuments = data?.projectManagement.project?._policies?.assignEditor.can || false
  const handleOnUploadClick = () => {
    setShowModal(true)
  }

  if (!hasPayingProjectsFeature) {
    return (
      <MissingFeatureEmptyState
        featureRequested={Features.projects_overview}
        requestSource={ProjectsUpsellRequestSources.projectsDocuments}
        description={i18n.t('project_management.upselling.files')}
        variant="table"
      />
    )
  }

  return (
    <>
      <ProjectDocumentsDataCollection
        projectId={numberProjectId}
        canManageDocuments={canManageDocuments}
        onClickUpload={handleOnUploadClick}
      />

      {showModal && (
        <DocumentsUploadWizard
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
          loading={createProjectDocumentLoading}
        />
      )}
    </>
  )
}

export default DocumentsPage
